/**
 * `Sidecar: SidecarApi` (W1-07b formats-package; contract formats/types.ts; formats-and-packaging.md P§6.5,
 * DESIGN.md §5.12, §7.3): phiedit.json, the editor-only data that travels inside a package next to chart.json.
 *
 * Layout: `{"format":"phiedit","version":1,"chartSha256":"<hex>","doc":<SerializedChartDoc>}` plus unknown root keys
 * of newer writers (kept as `extra` when read). The document is the full editor document without asset bytes
 * (groups, layer names, tags, xy links, ignored problems, data Phira cannot carry such as GIF progress on a non-GIF
 * texture or Bezier fields of speed events), anchored by the SHA-256 of the chart.json bytes written with it.
 *
 * `apply` (P0): when the anchor matches the imported chart.json, the stored document is restored EXACTLY ('exact');
 * when another program changed the chart (or the sidecar is unreadable, from a newer version, or its document does
 * not validate), the lifted document is kept and the report says why ('rejected', class 'flagged'). Re-anchoring
 * editor data onto a changed chart ('reanchored') is P1 and never returned here.
 */

import { deserializeDoc, serializeDoc } from '../../core/doc/serialize';
import type { ImportReportEntry } from '../../core/ir/types';
import type { ChartDoc } from '../../core/model/types';
import type { Json } from '../../core/util/types';
import type { PhieditSidecar, SidecarApi } from '../types';

/** File name of the sidecar at the package root. */
export const SIDECAR_FILE = 'phiedit.json';
export const SIDECAR_VERSION = 1;

const SHA256_RE = /^[0-9a-f]{64}$/;
const KNOWN = new Set(['format', 'version', 'chartSha256', 'doc']);

export type SidecarParse =
  | { readonly ok: true; readonly sidecar: Omit<PhieditSidecar, 'doc'> & { readonly doc: unknown } }
  | { readonly ok: false; readonly reason: 'notJson' | 'notSidecar' | 'newerVersion'; readonly detail?: string };

/** Reads phiedit.json without validating the document (see `apply`). */
export function parseSidecar(bytes: Uint8Array): SidecarParse {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes));
  } catch (e) {
    return { ok: false, reason: 'notJson', detail: e instanceof Error ? e.message : String(e) };
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return { ok: false, reason: 'notSidecar' };
  const o = value as Record<string, unknown>;
  if (o.format !== 'phiedit') return { ok: false, reason: 'notSidecar' };
  if (typeof o.version !== 'number' || !Number.isInteger(o.version) || o.version < 1) {
    return { ok: false, reason: 'notSidecar' };
  }
  if (o.version > SIDECAR_VERSION) return { ok: false, reason: 'newerVersion', detail: String(o.version) };
  if (typeof o.chartSha256 !== 'string' || !SHA256_RE.test(o.chartSha256)) return { ok: false, reason: 'notSidecar' };
  if (o.doc === null || typeof o.doc !== 'object') return { ok: false, reason: 'notSidecar' };
  const extra: Array<readonly [string, Json]> = [];
  for (const [k, v] of Object.entries(o)) if (!KNOWN.has(k)) extra.push([k, v as Json]);
  return {
    ok: true,
    sidecar: { format: 'phiedit', version: 1, chartSha256: o.chartSha256, doc: o.doc, extra },
  };
}

function write(doc: ChartDoc, chartSha256: string): Uint8Array {
  const body = { format: 'phiedit', version: SIDECAR_VERSION, chartSha256, doc: serializeDoc(doc) };
  return new TextEncoder().encode(JSON.stringify(body));
}

function rejected(
  doc: ChartDoc,
  key: string,
  params?: Readonly<Record<string, string | number>>,
): { doc: ChartDoc; mode: 'rejected'; report: readonly ImportReportEntry[] } {
  const message = params === undefined ? { key } : { key, params };
  return { doc, mode: 'rejected', report: [{ cls: 'flagged', message, path: SIDECAR_FILE }] };
}

function apply(
  liftedDoc: ChartDoc,
  sidecarBytes: Uint8Array,
  chartSha256: string,
): {
  readonly doc: ChartDoc;
  readonly mode: 'exact' | 'reanchored' | 'rejected';
  readonly report: readonly ImportReportEntry[];
} {
  const parsed = parseSidecar(sidecarBytes);
  if (!parsed.ok) {
    if (parsed.reason === 'newerVersion') {
      return rejected(liftedDoc, 'pkg.sidecar.newerVersion', { version: parsed.detail ?? '' });
    }
    return rejected(liftedDoc, 'pkg.sidecar.unreadable');
  }
  if (parsed.sidecar.chartSha256 !== chartSha256) return rejected(liftedDoc, 'pkg.sidecar.rejected');
  let doc: ChartDoc;
  try {
    doc = deserializeDoc(parsed.sidecar.doc);
  } catch (e) {
    return rejected(liftedDoc, 'pkg.sidecar.invalidDoc', { detail: e instanceof Error ? e.message : String(e) });
  }
  return {
    doc,
    mode: 'exact',
    report: [{ cls: 'exact', message: { key: 'pkg.sidecar.restored' }, path: SIDECAR_FILE }],
  };
}

export const Sidecar: SidecarApi = { write, apply };
